// @flow

import React from 'react';
import _ from 'lodash';
import memoize from 'memoize-weak';
import { connect } from 'react-redux';
import { connectRouteTo } from 'redux-router-kit';

import * as Account from 'app/entities/Account';
import * as Member from 'app/entities/Member';
import DashboardActions from 'app/dashboard/DashboardActions';
import FlowActions from 'app/beta-editor/FlowActions';
import TrackActions from 'app/track/TrackActions';
import Zap from 'app/dashboard/components/Zap';
import { isCurrentProfileAdminOfCurrentAccount } from 'app/account/Selectors';
import {
  SharedZapDisabledByTeamsForOwner,
  ZapDisabledByTeamsForMember,
} from 'app/dashboard/components/ZapDisabledMessages';
import { isZapOwned, selectZapIdsWithHeldTasks } from 'app/dashboard/Selectors';

import type { FolderIdType, ZapType } from 'app/dashboard/types';
import type { UnpauseOriginType } from 'app/beta-editor/types';
import { type Dispatch } from 'app/common/types';

type Owner = {
  avatarUrl: string,
  id: number | string,
  name: string,
};

type StateProps = {
  isOwned: boolean,
  owner: ?Owner,
};

const computeDisabledByTeamsMessage = memoize(
  (currentAccount, isCurrentProfileTeamAdmin, isSharedZap) => {
    const accountId = Account.selectors.id(currentAccount);
    const wasTeam = Account.selectors.wasTeam(currentAccount);
    if (wasTeam && !isCurrentProfileTeamAdmin) {
      return <ZapDisabledByTeamsForMember accountId={accountId} />;
    }
    if (wasTeam && isSharedZap) {
      return <SharedZapDisabledByTeamsForOwner />;
    }
    return null;
  }
);

const ownerToProps = memoize(zapOwner => {
  return {
    avatarUrl: Member.selectors.avatarUrl(zapOwner),
    id: Member.selectors.id(zapOwner),
    name: Member.selectors.normalizedName(zapOwner),
  };
});

const mapStateToProps = (state, props): StateProps => {
  const currentAccount = Account.selectors.all.currentAccount(state);

  const zapOwner = Member.selectors.all.entity(props.zap.owner_id, state);
  // Note that `zap.owner_id` is used here since non-team accounts won't
  // have any members and we therefore cannot depend on `owner` existing.
  const zapOwnerId = props.zap.owner_id;
  const isOwned = isZapOwned(zapOwnerId, state);
  const zapIdsWithHeldTasks = selectZapIdsWithHeldTasks(state);
  const hasHeldTasks = zapIdsWithHeldTasks.includes(props.zap.id);

  const disabledByTeamsMessage = computeDisabledByTeamsMessage(
    currentAccount,
    isCurrentProfileAdminOfCurrentAccount(state),
    props.zap.is_shared
  );

  // IMPORTANT: Any props going into `Zap` here need to be pure,
  // so use memoization if necessary. This is a high performance area!
  return {
    canSharePublicly: Account.selectors.canSharePublicly(currentAccount),
    disabledMessage: disabledByTeamsMessage,
    isDisabled: !!disabledByTeamsMessage,
    isDisabledByTeams: !!disabledByTeamsMessage,
    hasHeldTasks,
    isOwned,
    owner: !zapOwner ? null : ownerToProps(zapOwner),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  saveUpdateTitle: (id: number, title: string) =>
    dispatch(FlowActions.saveUpdateTitle(id, title)),
  move: (id: number, folderId: FolderIdType) =>
    dispatch(FlowActions.move(id, folderId)),
  copy: (zap: ZapType) => dispatch(FlowActions.copy(zap)),
  remove: (id: number) => dispatch(FlowActions.remove(id)),
  unpause: (id: number, origin: UnpauseOriginType) =>
    dispatch(FlowActions.unpause(id, origin)),
  pause: (id: number) => dispatch(FlowActions.pause(id)),
  runZap: (zapId: number) => dispatch(DashboardActions.runZap(zapId)),
  runZapStatus: (zapId: number) =>
    dispatch(DashboardActions.runZapStatus(zapId)),
  track: (message: string, data: *) => {
    dispatch(TrackActions.track(message, data));
  },
});

const ZapContainer = _.flowRight(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  connectRouteTo
)(Zap);

ZapContainer.displayName = 'ZapContainer';

export default ZapContainer;
